/**
 * JobSearchOptionsMenu Component
 * A dropdown menu component that provides job search filtering options including:
 * - Specialisation selection with nested subspecialisations
 * - State/region filtering
 * - Job type filtering
 * - Experience level filtering
 * - Salary range selection
 */
import { useClickAway } from "@uidotdev/usehooks";
import useMediaQuery from "@mui/material/useMediaQuery";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { calibrateSalary } from "../../../../helpers/data_management";
import { getJobTypes } from "../../../../redux/actions/job_action";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  BottomContainer,
  ButtonStyled,
  CheckboxButton,
  CheckboxIcon,
  CheckedBox,
  FilterCount,
  FilterSalaryContainer,
  FilterSpecialisationCount,
  GlobalHireContainer,
  GlobalHireSubtitle,
  InnerWrapper,
  MenuButton,
  MenuButtonDetail,
  MenuContainer,
  MenuCountContainer,
  MenuItem,
  MenuItemDetail,
  MenuItemList,
  MenuWrapper,
  SelectButton,
  SliderContainer,
  SliderStyled,
  TopContainer,
  UncheckedBox,
  Wrapper,
} from "./styles";

// Constants for Filter Keys - Used to identify different filter types
const FILTER_KEYS = {
  SPECIALISATION: "specialisation",
  STATE: "state",
  JOB_TYPES: "jobType",
  EXPERIENCE: "experience",
  SALARY: "salary",
};

// Maps filter keys to their corresponding display properties in the data
const ITEM_DISPLAY_KEYS = {
  [FILTER_KEYS.STATE]: "state",
  [FILTER_KEYS.JOB_TYPES]: "title",
  [FILTER_KEYS.EXPERIENCE]: "title",
};
const normalizeKey = (value) =>
  value
    ?.toString()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

// Main Component
export default function JobSearchOptionsMenu(props) {
  const {
    filterOpen,
    setFilterOpen,
    anchorRef,
    onApply,
    mode = "filter",
    locationSearch = "",
    menuRef: menuRefProp = null,
    onSelectionChange = null,
    stretchToParent = false,
    locationBorderColor = null,
    locationBoxShadow = null,
    locationBorderRadius = null,
  } = props;

  const internalMenuRef = useRef(null);
  const menuRef = menuRefProp || internalMenuRef;
  const wasLocationOpenRef = useRef(false);
  const dispatch = useDispatch();

  const specialisationList = useSelector((state) => state.jobs?.specialisation);
  const stateList = useSelector((state) => state.jobs?.stateRegions);
  const jobTypeList = useSelector((state) => state.jobs?.jobTypes);
  const experienceLevelList = useSelector(
    (state) => state.jobs?.experienceLevels
  );
  const jobListFilter = useSelector((state) => state.jobs?.jobListFilter);

  const [buttonLeft, setButtonLeft] = useState(0);
  const [openDropdown, setOpenDropdown] = useState("");
  const [selectedFilter, setSelectedFilter] = useState({});
  const [buttonWidth, setButtonWidth] = useState(0);
  const { t } = useTranslation();

  const isLocationMode = mode === "location";
  const isMobile = useMediaQuery("(max-width: 640px)");

  useEffect(() => {
    if (!jobTypeList?.length) {
      dispatch(getJobTypes());
    }
  }, [dispatch, jobTypeList?.length]);

  const filteredStateList = useMemo(() => {
    if (!locationSearch || mode !== "location") return stateList;
    const keyword = locationSearch.toLowerCase();
    return stateList?.filter((item) =>
      item?.state?.toLowerCase().includes(keyword)
    );
  }, [locationSearch, mode, stateList]);

  const calculateButtonPosition = useCallback(() => {
    if (!anchorRef?.current) return;

    const anchorRect = anchorRef.current.getBoundingClientRect();
    const menuContainerRect = menuRef.current?.parentElement?.getBoundingClientRect?.();
    const anchorParentRect =
      anchorRef.current.offsetParent?.getBoundingClientRect?.();
    const leftOffset = menuContainerRect
      ? anchorRect.left - menuContainerRect.left
      : anchorRect.left;

    setButtonLeft(leftOffset);

    if (anchorParentRect && isLocationMode && stretchToParent) {
      // Stretch the dropdown from the location input to the end of the bar
      const availableWidth = anchorParentRect.right - anchorRect.left;
      setButtonWidth(availableWidth);
      return;
    }

    setButtonWidth(anchorRect.width);
  }, [anchorRef, isLocationMode, menuRef, stretchToParent]);

  const FilterList = useMemo(() => {
    const locationFilter = {
      title: t("jobs.search_options.location"),
      filter: FILTER_KEYS.STATE,
      list: filteredStateList,
    };

    if (mode === "location") {
      return [locationFilter];
    }

    return [
      {
        title: t("jobs.search_options.specialisation"),
        filter: FILTER_KEYS.SPECIALISATION,
        list: specialisationList,
      },
      ...(isMobile ? [locationFilter] : []),
      {
        title: t("jobs.search_options.job_types"),
        filter: FILTER_KEYS.JOB_TYPES,
        list: jobTypeList,
      },
      {
        title: t("jobs.search_options.experience"),
        filter: FILTER_KEYS.EXPERIENCE,
        list: experienceLevelList,
      },
      {
        title: t("jobs.search_options.salary"),
        filter: FILTER_KEYS.SALARY,
      },
    ];
  }, [
    specialisationList,
    filteredStateList,
    jobTypeList,
    experienceLevelList,
    mode,
    isMobile,
    t,
  ]);

  // Memoized helper functions
  const isItemSelected = useCallback(
    (type, currentItem) => {
      if (type === FILTER_KEYS.STATE) {
        return selectedFilter?.[type]?.includes(currentItem?.state) || false;
      }
      return (
        selectedFilter?.[type]?.includes(parseInt(currentItem?.id)) || false
      );
    },
    [selectedFilter]
  );

  function handleOpenDropdown(filterKey) {
    setOpenDropdown(openDropdown === filterKey ? "" : filterKey);
  }

  function handleBulkSelect(type, list) {
    setSelectedFilter((prev) => {
      const nextSelection =
        prev[type]?.length > 0
          ? []
          : list.map((item) =>
              type === FILTER_KEYS.STATE ? item.state : parseInt(item.id)
            );
      const nextFilter = { ...prev, [type]: nextSelection };
      return nextFilter;
    });
  }

  function handleClickCheckbox(type, item) {
    setSelectedFilter((prev) => {
      const existingIds = prev[type] || [];
      const itemValue =
        type === FILTER_KEYS.STATE ? item.state : parseInt(item.id);
      const itemExists = existingIds.includes(itemValue);
      const nextFilter = {
        ...prev,
        [type]: itemExists
          ? existingIds.filter((id) => id !== itemValue)
          : [...existingIds, itemValue],
      };
      return nextFilter;
    });
  }

  function handleClearFilter(filterType = null) {
    let clearedFilter;

    if (filterType === FILTER_KEYS.SALARY) {
      // Only clear salary when specifically clearing the salary filter
      clearedFilter = { ...selectedFilter, salary: 0 };
    } else if (filterType) {
      // Clear the specific filter type
      clearedFilter = { ...selectedFilter, [filterType]: [] };
    } else {
      // Clear all filters (when filterType is null)
      clearedFilter = {
        specialisation: [],
        state: [],
        jobType: [],
        experience: [],
        salary: 0,
        globalHire: false,
        keyword: selectedFilter.keyword || "",
      };
    }

    setSelectedFilter(clearedFilter);
    onApply(clearedFilter);
    setFilterOpen(false);
  }

  // Close the menu when clicking away
  useClickAway(menuRef, () => {
    if (filterOpen) {
      setFilterOpen(false);
    }
  });

  // Position the menu relative to the filter button
  useEffect(() => {
    // Calculate initial position
    calculateButtonPosition();

    // Add resize listener
    window.addEventListener("resize", calculateButtonPosition);

    // Cleanup
    return () => {
      window.removeEventListener("resize", calculateButtonPosition);
    };
  }, [calculateButtonPosition]);

  useEffect(() => {
    if (!filterOpen) return;

    // Recalculate on every open/click so position/width follows the latest layout.
    calculateButtonPosition();

    const rafId = window.requestAnimationFrame(calculateButtonPosition);
    return () => window.cancelAnimationFrame(rafId);
  }, [filterOpen, mode, calculateButtonPosition]);

  // Reset dropdown when filter active changes
  useEffect(() => {
    if (filterOpen) {
      setOpenDropdown(mode === "location" ? FILTER_KEYS.STATE : "");
    } else {
      setOpenDropdown("");
    }
  }, [filterOpen, mode]);

  useEffect(() => {
    setSelectedFilter(jobListFilter);
  }, [jobListFilter]);

  useEffect(() => {
    if (typeof onSelectionChange === "function") {
      onSelectionChange(selectedFilter);
    }
  }, [onSelectionChange, selectedFilter]);

  useEffect(() => {
    const wasLocationOpen = wasLocationOpenRef.current;
    const isLocationOpen = filterOpen && isLocationMode;

    if (wasLocationOpen && !isLocationOpen) {
      const previousStates = jobListFilter?.state || [];
      const nextStates = selectedFilter?.state || [];
      const hasLocationChanged =
        previousStates.length !== nextStates.length ||
        previousStates.some((state) => !nextStates.includes(state)) ||
        nextStates.some((state) => !previousStates.includes(state));

      if (hasLocationChanged) {
        onApply(selectedFilter);
      }
    }

    wasLocationOpenRef.current = isLocationOpen;
  }, [filterOpen, isLocationMode, jobListFilter, onApply, selectedFilter]);

  // Memoized render functions
  const renderFilterContent = useCallback(
    (filter) => {
      const commonProps = {
        selectedFilter,
        setSelectedFilter,
        onApply,
        handleClearFilter,
      };

      if (filter.filter === FILTER_KEYS.SALARY) {
        return <FilterSalaryItem {...commonProps} />;
      }

      if (filter.filter === FILTER_KEYS.SPECIALISATION) {
        return (
          <FilterSpecialisationItem
            {...commonProps}
            specList={filter.list}
            handleClickCheckbox={handleClickCheckbox}
            isItemSelected={isItemSelected}
            t={t}
          />
        );
      }

      return (
        <FilterDefaultItem
          {...commonProps}
          list={filter.list}
          filterKey={filter.filter}
          handleBulkSelect={handleBulkSelect}
          handleClickCheckbox={handleClickCheckbox}
          isItemSelected={isItemSelected}
          showActions={!isLocationMode}
          t={t}
        />
      );
    },
    [
      selectedFilter,
      handleClickCheckbox,
      handleBulkSelect,
      isItemSelected,
      onApply,
      handleClearFilter,
      isLocationMode,
      t,
    ]
  );

  return (
    <Wrapper
      ref={menuRef}
      $open={filterOpen}
      $buttonLeft={buttonLeft}
      $width={isLocationMode && buttonWidth ? buttonWidth : undefined}
      $borderless={mode === "location"}
    >
      <InnerWrapper $compact={isLocationMode}>
        {mode !== "location" && (
          <GlobalHireFilter
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
            onApply={onApply}
            t={t}
          />
        )}
        {FilterList.map((filter) =>
          mode === "location" ? (
            <MenuWrapper key={filter.filter}>
              <MenuContainer
                $open
                $selected
                $borderColor={locationBorderColor}
                $selectedBorderColor={locationBorderColor}
                $boxShadow={locationBoxShadow}
                $borderRadius={locationBorderRadius}
              >
                {renderFilterContent(filter)}
              </MenuContainer>
            </MenuWrapper>
          ) : (
            <MenuWrapper key={filter.filter}>
              <MenuTitleComponent
                filterKey={filter.filter}
                title={filter.title}
                selectedFilter={selectedFilter}
                openDropdown={openDropdown}
                handleOpenDropdown={handleOpenDropdown}
              />
              <MenuContainer
                $open={openDropdown === filter.filter}
                $selected={openDropdown === filter.filter}
              >
                {renderFilterContent(filter)}
              </MenuContainer>
            </MenuWrapper>
          )
        )}
      </InnerWrapper>
    </Wrapper>
  );
}

// Reusable Bottom Action Buttons component
function BottomActions({ onClear, onApply, selectedFilter, t }) {
  return (
    <BottomContainer>
      <ButtonStyled $outlined onClick={onClear}>
        {t("jobs.search_options.clear")}
      </ButtonStyled>
      <ButtonStyled onClick={() => onApply(selectedFilter)} data-testid="job-filter-apply-btn">
        {t("jobs.search_options.apply")}
      </ButtonStyled>
    </BottomContainer>
  );
}

const GlobalHireFilter = memo(
  ({ selectedFilter, setSelectedFilter, onApply, t }) => {
    const [globalHireState, setGlobalHireState] = useState(false);

    function handleClickCheckbox() {
      if (globalHireState) {
        const newSelectedFilter = { ...selectedFilter, globalHire: false };
        setSelectedFilter(newSelectedFilter);
        onApply(newSelectedFilter);
      } else {
        const newSelectedFilter = { ...selectedFilter, globalHire: true };
        setSelectedFilter(newSelectedFilter);
        onApply(newSelectedFilter);
      }
    }

    useEffect(() => {
      setGlobalHireState(selectedFilter.globalHire);
    }, [selectedFilter.globalHire]);

    return (
      <MenuButton
        $globalHire
        $selected={globalHireState}
        onClick={handleClickCheckbox}
      >
        <CheckboxButton
          checked={globalHireState}
          icon={<UncheckedBox />}
          checkedIcon={
            <CheckedBox>
              <CheckboxIcon />
            </CheckedBox>
          }
          onChange={() => {}} // Add empty onChange to avoid React warning
        />
        <GlobalHireContainer>
          <MenuButtonDetail>
            {t("jobs.search_options.global_hire_only")}
          </MenuButtonDetail>
          <GlobalHireSubtitle>
            {t("jobs.search_options.global_hire_subtitle")}
          </GlobalHireSubtitle>
        </GlobalHireContainer>
      </MenuButton>
    );
  }
);

// Memoized MenuTitleComponent
const MenuTitleComponent = memo(
  ({ title, filterKey, selectedFilter, openDropdown, handleOpenDropdown }) => {

    const filterLength = useMemo(() => {
      return selectedFilter?.[filterKey]?.length || 0;
    }, [selectedFilter, filterKey]);

    const salaryAmount = useMemo(() => {
      if (filterKey !== FILTER_KEYS.SALARY || !selectedFilter?.salary) return null;
      return process.env.NEXT_PUBLIC_JSW_GEOLOCATION === "my"
        ? "RM " + calibrateSalary(selectedFilter.salary)
        : calibrateSalary(selectedFilter.salary) + " SGD";
    }, [selectedFilter?.salary, filterKey]);

    return (
      <MenuButton
        data-testid={`job-filter-type-btn-${filterKey}`}
        $open={openDropdown === filterKey}
        $selected={openDropdown === filterKey}
        onClick={() => handleOpenDropdown(filterKey)}
      >
        <MenuButtonDetail>
          {title}
          {filterLength > 0 && <FilterCount>{filterLength}</FilterCount>}
          {salaryAmount && <FilterCount $salary>{salaryAmount}</FilterCount>}
        </MenuButtonDetail>
        <ArrowUpIcon $open={openDropdown === title} />
      </MenuButton>
    );
  }
);

// Memoized FilterSpecialisationItem
const FilterSpecialisationItem = memo(
  ({
    specList,
    selectedFilter,
    setSelectedFilter,
    handleClickCheckbox,
    isItemSelected,
    onApply,
    handleClearFilter,
    t,
  }) => {
    const [subSpecList, setSubSpecList] = useState([]);
    const fullSubSpecList = useMemo(
      () => specList?.flatMap((spec) => spec?.subSpecialisations) || [],
      [specList]
    );

    const selectText = useMemo(() => {
      if (subSpecList?.length > 0) {
        return subSpecList.some((subSpec) =>
          isItemSelected(FILTER_KEYS.SPECIALISATION, subSpec)
        )
          ? t("jobs.search_options.deselect_all")
          : t("jobs.search_options.select_all");
      }
      return selectedFilter?.specialisation?.length > 0
        ? t("jobs.search_options.deselect_all")
        : t("jobs.search_options.select_all");
    }, [subSpecList, selectedFilter?.specialisation, isItemSelected, t]);

    function handleBulkSelectSpec() {
      setSelectedFilter((prev) => ({
        ...prev,
        specialisation:
          prev.specialisation?.length > 0
            ? prev.specialisation.filter(
                (id) => !fullSubSpecList.some((spec) => spec.id === id)
              )
            : fullSubSpecList.map((item) => item.id),
      }));
    }

    function handleBulkSelectSubSpec() {
      setSelectedFilter((prev) => {
        const anySelected = subSpecList.some((subSpec) =>
          prev.specialisation?.some((id) => id === subSpec.id)
        );

        if (anySelected) {
          return {
            ...prev,
            specialisation: prev.specialisation?.filter(
              (id) => !subSpecList.some((subSpec) => subSpec.id === id)
            ),
          };
        }

        const currentSelected = prev.specialisation || [];
        const newIds = subSpecList
          .filter((subSpec) => !currentSelected.includes(subSpec.id))
          .map((item) => item.id);
        return {
          ...prev,
          specialisation: [...currentSelected, ...newIds],
        };
      });
    }

    return (
      <>
        {subSpecList?.length > 0 ? (
          <>
            <TopContainer>
              <SelectButton onClick={() => setSubSpecList([])}>
                <ArrowLeftIcon />
              </SelectButton>
              <SelectButton onClick={handleBulkSelectSubSpec}>
                {selectText}
              </SelectButton>
            </TopContainer>
            <MenuItemList>
              {subSpecList?.map((subSpecItem) => (
                <MenuItem
                  key={subSpecItem.id}
                  onClick={() =>
                    handleClickCheckbox(FILTER_KEYS.SPECIALISATION, subSpecItem)
                  }
                >
                  <MenuItemDetail>
                    <CheckboxButton
                      icon={<UncheckedBox />}
                      checked={isItemSelected(
                        FILTER_KEYS.SPECIALISATION,
                        subSpecItem
                      )}
                      checkedIcon={
                        <CheckedBox>
                          <CheckboxIcon />
                        </CheckedBox>
                      }
                    />
                    {t(
                      `companydetail.specialisation_labels.${normalizeKey(
                        subSpecItem?.name
                      )}`,
                      { defaultValue: subSpecItem?.name }
                    )}
                  </MenuItemDetail>
                </MenuItem>
              ))}
            </MenuItemList>
          </>
        ) : (
          <>
            <TopContainer>
              <SelectButton onClick={handleBulkSelectSpec}>
                {selectText}
              </SelectButton>
            </TopContainer>
            <MenuItemList>
              {specList?.map((item) => (
                <SpecialisationTitleComponent
                  key={item.id}
                  item={item}
                  selectedFilter={selectedFilter}
                  handleSpecClick={setSubSpecList}
                  t={t}
                />
              ))}
            </MenuItemList>
          </>
        )}
        <BottomActions
          onClear={() => handleClearFilter(FILTER_KEYS.SPECIALISATION)}
          onApply={onApply}
          selectedFilter={selectedFilter}
          t={t}
        />
      </>
    );
  }
);

// Memoized SpecialisationTitleComponent
const SpecialisationTitleComponent = memo(
  ({ item, selectedFilter, handleSpecClick, t }) => {
    const filterSpecialisationLength = useMemo(() => {
      if (
        !selectedFilter?.specialisation?.length ||
        selectedFilter?.specialisation?.length === 0 ||
        !item?.subSpecialisations?.length
      ) {
        return 0;
      }
      return selectedFilter.specialisation.filter((id) =>
        item.subSpecialisations.some((subSpec) => subSpec.id === id)
      ).length;
    }, [
      selectedFilter?.specialisation,
      item?.subSpecialisations,
      selectedFilter?.specialisation?.length,
    ]);

    return (
      <MenuItem onClick={() => handleSpecClick(item?.subSpecialisations)}>
        <MenuItemDetail>
          {t(
            `companydetail.specialisation_labels.${normalizeKey(item?.name)}`,
            { defaultValue: item?.name }
          )}
        </MenuItemDetail>
        <MenuCountContainer>
          {filterSpecialisationLength > 0 && (
            <FilterSpecialisationCount>
              {filterSpecialisationLength}
            </FilterSpecialisationCount>
          )}
          <ArrowRightIcon />
        </MenuCountContainer>
      </MenuItem>
    );
  }
);

// Memoized FilterDefaultItem
const FilterDefaultItem = memo(
  ({
    list,
    filterKey,
    selectedFilter,
    handleBulkSelect,
    handleClickCheckbox,
    isItemSelected,
    onApply,
    handleClearFilter,
    showActions = true,
    t,
  }) => {
    const selectText =
      selectedFilter?.[filterKey]?.length > 0
        ? t("jobs.search_options.deselect_all")
        : t("jobs.search_options.select_all");
    const itemKey = ITEM_DISPLAY_KEYS[filterKey];
    const getItemLabel = (item) => {
      const rawValue = item?.[itemKey];
      if (!rawValue) return "";
      if (filterKey === FILTER_KEYS.STATE) {
        return t(`companies.state_region_labels.${normalizeKey(rawValue)}`, {
          defaultValue: rawValue,
        });
      }
      if (filterKey === FILTER_KEYS.JOB_TYPES) {
        return t(`companydetail.job_type_labels.${normalizeKey(rawValue)}`, {
          defaultValue: rawValue,
        });
      }
      if (filterKey === FILTER_KEYS.EXPERIENCE) {
        return t(`userprofile.job_alerts_create_dialog.experience_labels.${normalizeKey(rawValue)}`, {
          defaultValue: rawValue,
        });
      }
      return rawValue;
    };

    return (
      <>
        <TopContainer>
          <SelectButton onClick={() => handleBulkSelect(filterKey, list)}>
            {selectText}
          </SelectButton>
        </TopContainer>
        <MenuItemList>
          {list?.map((item) => (
            <MenuItem
              key={item.id}
              onClick={() => handleClickCheckbox(filterKey, item)}
            >
              <MenuItemDetail>
                <CheckboxButton
                  icon={<UncheckedBox />}
                  checked={isItemSelected(filterKey, item)}
                  checkedIcon={
                    <CheckedBox>
                      <CheckboxIcon />
                    </CheckedBox>
                  }
                />
                {getItemLabel(item)}
              </MenuItemDetail>
            </MenuItem>
          ))}
        </MenuItemList>
        {showActions ? (
          <BottomActions
            onClear={() => handleClearFilter(filterKey)}
            onApply={onApply}
            selectedFilter={selectedFilter}
            t={t}
          />
        ) : null}
      </>
    );
  }
);

// Memoized FilterSalaryItem
const FilterSalaryItem = memo(
  ({ selectedFilter, setSelectedFilter, onApply, handleClearFilter }) => {
    const { t } = useTranslation();
    const salary = selectedFilter?.salary || 0;
    const salaryAmount = useMemo(
      () =>
        process.env.NEXT_PUBLIC_JSW_GEOLOCATION === "my"
          ? "RM " + calibrateSalary(selectedFilter?.salary)
          : calibrateSalary(selectedFilter?.salary) + " SGD",
      [selectedFilter?.salary]
    );

    const handleSelectSalary = useCallback(
      (e) => {
        setSelectedFilter((prev) => ({
          ...prev,
          salary: e.target.value,
        }));
      },
      [setSelectedFilter]
    );

    return (
      <>
        <FilterSalaryContainer>
          {t("jobs.search_options.minimum_salary")}
          <SliderContainer>
            <SliderStyled
              aria-labelledby="salary-range-slider"
              step={1}
              marks
              min={0}
              max={50}
              onChange={handleSelectSalary}
              value={salary}
            />
          </SliderContainer>
          {salaryAmount}
        </FilterSalaryContainer>
        <BottomActions
          onClear={() => handleClearFilter(FILTER_KEYS.SALARY)}
          onApply={onApply}
          selectedFilter={selectedFilter}
          t={t}
        />
      </>
    );
  }
);
